import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserIdentity } from '../types/user-identity';
import type { Language, LocaleOption } from '../types/locale';

type PortalStore = {
  locale: string;
  language: Language;
  userIdentity: UserIdentity | null;
  availableLocales: LocaleOption[];
  setLocale: (locale: string) => void;
  setLanguage: (language: Language) => void;
  setUserIdentity: (identity: UserIdentity | null) => void;
  setAvailableLocales: (locales: LocaleOption[]) => void;
  clearUserIdentity: () => void;
};

/**
 * Zustand store for portal-wide session state: locale, language override, user identity, and available locales.
 *
 * Persisted to localStorage under '@lucid/portal-core' so it survives page reloads without
 * conflicting with the app's own store key ('theme-storage').
 *
 * Initial values are placeholders — PortalCoreProvider overrides them with the
 * app-specific defaultLocale / defaultLanguage from createPortalConfig().
 * availableLocales are computed by the provider when user identity syncs and a role filter is present.
 */
export const usePortalStore = create<PortalStore>()(
  persist(
    (set) => ({
      locale: 'de-de',
      language: null,
      userIdentity: null,
      availableLocales: [],

      setLocale: (locale) => set({ locale }),
      setLanguage: (language) => set({ language }),
      setUserIdentity: (identity) => set({ userIdentity: identity }),
      setAvailableLocales: (locales) => set({ availableLocales: locales }),
      clearUserIdentity: () => set({ userIdentity: null }),
    }),
    {
      name: '@lucid/portal-core',
      partialize: (state) => ({
        locale: state.locale,
        language: state.language,
        userIdentity: state.userIdentity,
      }),
    }
  )
);

/**
 * Access portal store state outside of React components.
 * Safe to use in API interceptors, utilities, and other non-React contexts.
 */
export const getPortalStoreState = () => usePortalStore.getState();
